<template>
  <section class="w-full" :class="sectionOuterSpacingClass(data)">
    <div
      class="w-full"
      :class="statTopBgClass"
    >
      <div class="container">
        <!-- title -->
        <UiHeading
          v-if="data?.heading"
          :pin="data?.heading?.pin"
          :title="data?.heading?.title"
          :description="data?.heading?.description"
          :button="data?.heading?.button"
          :color="data?.heading?.color"
          :position="data?.heading?.position"
          v-aos="animate && 'zoom-out'"
        />
        <!-- title -->
      </div>
    </div>

    <!-- Stats -->
    <div
      class="-mt-20 lg:-mt-36"
      :class="data?.backgroundColor === 'transparent' ? '' : 'pb-2 lg:pb-4'"
    >
      <div class="container px-0 md:px-4">
        <!-- Slider -->
        <template v-if="shouldShowSlider">
          <div v-aos="animate && 'fade-up'" class="w-full">
            <PluginsCarousel
              :data="data?.items"
              :pagination="true"
              :navigation="false"
              :per="data?.column === 'column-4' ? 4 : 3"
              type="Deck"
              effect="cube"
              class="cards-slider stats-slider"
              :class="carouselClass"
            >
              <template v-slot="{ slide, index }">
                <div
                  v-aos="animate && statAnimation(index)"
                  class="flex flex-col items-center gap-2 lg:gap-3 w-full h-full"
                  :class="statItemClass"
                  v-if="slide?.enabled"
                >
                  <AtomsImage
                    v-if="slide?.icon?.mime?.includes('image')"
                    class="!w-16 md:!w-12 lg:!w-16 xl:!w-20 mb-4"
                    :media="slide?.icon"
                    loading="eager"
                    sizeHint="half"
                    :alt="slide?.icon?.alternativeText ?? slide?.icon?.hash ?? slide?.icon?.name"
                  />
                  <div
                    class="text-center text-4xl xl:text-5xl font-black text-brand-first font-saira tracking-tight leading-none py-1"
                  >
                    {{ slide?.number }}
                  </div>
                  <div class="text-center font-bold text-gray-900 text-lg">
                    {{ slide?.title }}
                  </div>
                  <div class="text-center text-gray-500 text-[14px] font-medium leading-relaxed max-w-[240px]">
                    {{ slide?.description }}
                  </div>
                </div>
              </template>
            </PluginsCarousel>
          </div>
        </template>
        <!-- Slider -->

        <div v-else class="flex flex-wrap justify-center gap-6 lg:gap-8">
          <template v-for="(item, index) in data?.items">
            <div
              v-aos="animate && statAnimation(index)"
              class="flex flex-col items-center gap-2 lg:gap-3 w-80 md:w-72 2xl:w-80"
              :class="statItemClass"
              v-if="item?.enabled"
            >
              <AtomsImage
                v-if="item?.icon?.mime?.includes('image')"
                class="!w-16 md:!w-12 lg:!w-16 xl:!w-20 mb-4"
                :media="item?.icon"
                loading="eager"
                sizeHint="half"
                :alt="item?.icon?.alternativeText ?? item?.icon?.hash ?? item?.icon?.name"
              />
              <div
                class="text-center text-4xl xl:text-5xl font-black text-brand-first font-saira tracking-tight leading-none py-1"
              >
                {{ item?.number }}
              </div>
              <div class="text-center font-bold text-gray-900 text-lg">
                {{ item?.title }}
              </div>
              <div class="text-center text-gray-500 text-[14px] font-medium leading-relaxed max-w-[240px]">
                {{ item?.description }}
              </div>
            </div>
          </template>
          <!-- Stats -->
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { useMediaQuery } from "@vueuse/core";
import { useIsMobile768 } from "~/composables/utils";
import { computed } from "vue";
import { useSiteSettingsStore } from "@/store/index";
import { sectionOuterSpacingClass } from "~/composables/sectionSpacing";

const props = defineProps({
  data: { type: Object },
  animate: { type: Boolean, default: false },
});

const { isMobile768 } = useIsMobile768();
const isDesktop = useMediaQuery("(min-width: 1280px)", false);
const siteSettingsStore = useSiteSettingsStore();

const shouldShowSlider = computed(() => {
  return props.data?.slider || isMobile768.value;
});

const statAnimation = (index) => {
  if (isMobile768.value) return "fade-up";
  if (isDesktop.value) return statEnterClass(index);
  return null;
};

const statTopBgClass = computed(() => {
  const bg = props.data?.backgroundColor;
  const common = 'pt-10 lg:pt-20 pb-20 lg:pb-36';
  if (bg === 'first') return `bg-brand-first ${common}`;
  if (bg === 'second') return `bg-brand-second ${common}`;
  if (bg === 'third') return `bg-brand-third ${common}`;
  if (bg === 'dark-first') return `bg-brand-dark ${common}`;
  if (bg === 'dark-second') return `bg-brand-dark-second ${common}`;
  if (bg === 'light-first') return `bg-brand-light ${common}`;
  if (bg === 'light-second') return `bg-brand-light-second ${common}`;
  return 'pb-20 lg:pb-36';
});

const carouselClass = computed(() => {
  const bg = props.data?.backgroundColor;
  return (bg === 'first' || bg === 'second' || bg === 'dark-first' || bg === 'dark-second') ? '' : 'non-bg';
});

const statItemClass = computed(() => {
  return [
    'rounded-2xl lg:rounded-3xl hover:-translate-y-1 transition-all duration-300',
    'p-8 lg:p-12 bg-white border border-gray-50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]'
  ];
});

const statEnterClass = (index) => {
  if (index === 0) return "slide-right";
  if (index === 3) return "slide-left";
  return "slide-up";
};
</script>
